import { useState, useEffect } from 'react';

/**
 * Lightweight "is the visitor probably signed in?" check for PUBLIC marketing
 * pages — without booting the Clerk SDK.
 *
 * Why this exists: ClerkProvider used to wrap the whole app at the root, which
 * meant every public page (home, blogs, all landing pages) loaded clerk-js and
 * made a /v1/environment + /v1/client handshake to clerk.adstronaut.net just so
 * the nav could decide between "Sign in" and "Dashboard". That handshake was
 * burning a large share of Googlebot's crawl budget (the clerk.* subdomain was
 * ~14% of all crawl requests) and inflating the JSON crawl share.
 *
 * Clerk sets a NON-httpOnly cookie `__client_uat` on the eTLD+1 (.adstronaut.net)
 * specifically so first-party marketing surfaces can read sign-in state cheaply:
 *   __client_uat = 0   → signed out
 *   __client_uat > 0   → signed in (value is the session-update unix timestamp)
 * Reading it requires no network call and no provider, so public pages stay
 * Clerk-free. Auth-gated routes still use the real Clerk hooks inside
 * <ClerkBoundary> (see src/components/ClerkBoundary.jsx).
 */
export function getSessionHint() {
  if (typeof document === 'undefined') return false;
  const match = document.cookie.match(/(?:^|;\s*)__client_uat=(\d+)/);
  return !!match && parseInt(match[1], 10) > 0;
}

export default function useSessionHint() {
  // Initialise from the cookie synchronously so the first paint is already
  // correct for logged-in users (avoids a "Sign in" → "Dashboard" flicker).
  const [isSignedIn, setIsSignedIn] = useState(getSessionHint);

  useEffect(() => {
    // Re-read after mount in case the cookie was set between module eval and
    // hydration (e.g. crawler-prerendered HTML hydrating in a logged-in tab).
    setIsSignedIn(getSessionHint());
  }, []);

  return isSignedIn;
}
